import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import Youtube from "@tiptap/extension-youtube";
import ImageLink from "tiptap-extension-image-link";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { createContext, useContext, useRef, useEffect } from "react";
import { CustomButton } from "./extensions/CustomButton";
import { CustomImage } from "./extensions/CustomImage";
import { CustomTable } from "./extensions/CustomTable";
import { FontSize } from "./extensions/FontSize";
import LinkBubbleMenuHandler from "./extensions/LinkBubbleMenuHandler";
import LinkBubbleMenu from "./menu/LinkBubbleMenu";
import TableBubbleMenu from "./menu/TableBubbleMenu";
import ToolbarBase from "./toolbar/ToolbarBase";

const TiptapEditorContext = createContext(null);

export function useEditorContext() {
  const context = useContext(TiptapEditorContext);
  if (!context) {
    throw new Error(
      "A useEditorContext csak a TiptapEditorContext.Provider-en belül használható"
    );
  }

  return context;
}

const Wysiwyg = ({
  value = "",
  onChange,
  toolbar = () => <ToolbarBase />,
  readOnly = false,
  onImageUpload,
  onImageRemove,
  placeholder = "Kezdj el gépelni...",
  ...rest
}) => {
  const previousState = useRef();

  const editor = useEditor({
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: "form-control tiptap-editor",
        spellcheck: false,
      },
    },
    extensions: [
      StarterKit.configure({}),
      Underline.configure({}),
      TextStyle.configure({}),
      FontSize.configure({}),
      CustomTable.configure({}),
      TableRow.configure({}),
      TableHeader.configure({}),
      TableCell.configure({}),
      Image.configure({ allowBase64: true }),
      ImageLink.configure({
        HTMLAttributes: {
          class: "image-link",
        },
      }),
      CustomImage.configure({}),
      CustomButton.configure({ HTMLAttributes: { type: "button" } }),
      Placeholder.configure({ placeholder }),
      Color.configure({}),
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Youtube.configure({
        inline: false,
        enableIFrameApi: "true",
      }),
      Link.configure({
        protocols: ["ftp", "mailto", "tel"],
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      LinkBubbleMenuHandler.configure({}),
    ],
    content: value,
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());

      if (onImageRemove) {
        const prevNodesById = {};
        previousState.current?.doc.forEach((node) => {
          if (node.attrs.id) prevNodesById[node.attrs.id] = node;
        });

        const nodesById = {};
        editor.state.doc.forEach((node) => {
          if (node.attrs.id) nodesById[node.attrs.id] = node;
        });

        previousState.current = editor.state;

        for (const [id, node] of Object.entries(prevNodesById)) {
          if (nodesById[id] === undefined) {
            if (node.type.name === "image") {
              onImageRemove?.(node);
            }
          }
        }
      }
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  return (
    <TiptapEditorContext.Provider value={{ editor, onImageUpload }}>
      <div className="flex flex-col">
        {editor && (
          <>
            {toolbar?.()}
            <TableBubbleMenu />
            <LinkBubbleMenu />
          </>
        )}
        <EditorContent editor={editor} {...rest} />
      </div>
    </TiptapEditorContext.Provider>
  );
};

export default Wysiwyg;
